import i18n from "i18n";
import { action, observable } from "mobx";

import type { UsersAPI } from "generated/types";

import { ToastMethods } from "components/ToastNotification";
import { inviteRepository } from "repositories";

interface InviteStoreVariables {
  loading: boolean;
  emailInviteBundleDetails?: UsersAPI.EmailInviteBundleDetails | null;
  linkInviteDetails?: UsersAPI.InviteLinkDetails | null;
  createdInvitationLink?: UsersAPI.CreateInvitationLinkResponse | null;
  inviteLinkLicenseInfo?: UsersAPI.InviteLinkLicenseInfo | null;
}

interface InviteStoreSetters {
  setLoading: (isLoading: boolean) => void;
  setEmailInviteBundleDetails: (bundleDetails: UsersAPI.EmailInviteBundleDetails | null) => void;
  setLinkInviteDetails: (linkDetails: UsersAPI.InviteLinkDetails | null) => void;
  setCreatedInvitationLink: (invitationLink: UsersAPI.CreateInvitationLinkResponse | null) => void;
  setInviteLinkLicenseInfo: (licenseInfo: UsersAPI.InviteLinkLicenseInfo | null) => void;
}

interface InviteApiActions {
  fetchEmailInviteBundleDetails: (emailInviteBundleId: string) => Promise<void>;
  fetchLinkInviteDetails: (linkInviteId: string) => Promise<void>;
  addInvitesToEmailInvitationBundle: (
    emailInviteBundleId: string,
    targetEmails: string[],
  ) => Promise<UsersAPI.AddEmailInviteToBundleResponse>;
  createInvitationLink: (
    payload: UsersAPI.CreateInvitationLinkRequest,
  ) => Promise<UsersAPI.CreateInvitationLinkResponse | undefined>;
  updateInvitationLink: (
    inviteLinkId: string,
    payload: UsersAPI.UpdateInviteLinkRequest,
  ) => Promise<boolean>;
  fetchInviteLinkLicenseInfo: (inviteLinkId: string) => Promise<boolean>;
  claimInviteLinkLicense: (inviteLinkId: string) => Promise<{ success: boolean; errCode?: string }>;
}

type InviteStoreManagementProps = InviteStoreVariables & InviteStoreSetters & InviteApiActions;

const initialState: InviteStoreVariables = {
  loading: false,
  emailInviteBundleDetails: null,
  linkInviteDetails: null,
  createdInvitationLink: null,
  inviteLinkLicenseInfo: null,
};

const stateSetters: InviteStoreSetters = {
  setLoading: action((loading: boolean) => {
    store.loading = loading;
  }),
  setEmailInviteBundleDetails: action((bundleDetails: UsersAPI.EmailInviteBundleDetails | null) => {
    store.emailInviteBundleDetails = bundleDetails;
  }),
  setLinkInviteDetails: action((linkDetails: UsersAPI.InviteLinkDetails | null) => {
    store.linkInviteDetails = linkDetails;
  }),
  setCreatedInvitationLink: action(
    (invitationLink: UsersAPI.CreateInvitationLinkResponse | null) => {
      store.createdInvitationLink = invitationLink;
    },
  ),
  setInviteLinkLicenseInfo: action((licenseInfo: UsersAPI.InviteLinkLicenseInfo | null) => {
    store.inviteLinkLicenseInfo = licenseInfo;
  }),
};

const apiRequests: InviteApiActions = {
  fetchEmailInviteBundleDetails: action((emailInviteBundleId) => {
    store.setLoading(true);
    return inviteRepository
      .fetchEmailInviteBundleDetails(emailInviteBundleId)
      .then((bundleDetails: UsersAPI.EmailInviteBundleDetails) => {
        store.setEmailInviteBundleDetails(bundleDetails);
      })
      .catch(() => {
        ToastMethods.showToast(
          i18n.t("toast:studentLicense.getEmailInviteBundleDetails.error"),
          "error",
        );
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  fetchLinkInviteDetails: action((linkInviteId) => {
    store.setLoading(true);
    return inviteRepository
      .fetchLinkInviteDetails(linkInviteId)
      .then((linkDetails: UsersAPI.InviteLinkDetails) => {
        store.setLinkInviteDetails(linkDetails);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:studentLicense.getLinkInviteDetails.error"), "error");
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  // Create a new invitation link
  createInvitationLink: action((payload: UsersAPI.CreateInvitationLinkRequest) => {
    store.setLoading(true);
    return inviteRepository
      .createInvitationLink(payload)
      .then((invitationLink: UsersAPI.CreateInvitationLinkResponse) => {
        store.setCreatedInvitationLink(invitationLink);
        return invitationLink;
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:studentLicense.createInvitationLink.error"), "error");
        return undefined;
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  // Update settings for an existing invitation link
  updateInvitationLink: action(
    (inviteLinkId: string, payload: UsersAPI.UpdateInviteLinkRequest) => {
      store.setLoading(true);
      return inviteRepository
        .updateInvitationLink(inviteLinkId, payload)
        .then((invitationLink: UsersAPI.InviteLinkDetails) => {
          store.setLinkInviteDetails(invitationLink);
          ToastMethods.showToast(
            i18n.t("toast:studentLicense.updateInvitationLink.success"),
            "success",
          );
          return true;
        })
        .catch(() => {
          ToastMethods.showToast(
            i18n.t("toast:studentLicense.updateInvitationLink.error"),
            "error",
          );
          return false;
        })
        .finally(() => {
          store.setLoading(false);
        });
    },
  ),
  // Add additional invitations to an existing email invite bundle
  addInvitesToEmailInvitationBundle: action((emailInviteBundleId, targetEmails = []) => {
    store.setLoading(true);
    return inviteRepository
      .addInviteToEmailInvitationBundle(emailInviteBundleId, targetEmails)
      .then((result: UsersAPI.AddEmailInviteToBundleResponse) => {
        const { success: addedEmails, failed: notAddedEmails } = result;

        if (addedEmails.length > 0) {
          ToastMethods.showToast(
            i18n.t("toast:studentLicense.addInvitesToEmailInvitationBundle.success", {
              targetEmails: addedEmails.join(", "),
            }),
            "success",
          );
        }

        if (notAddedEmails.length > 0) {
          ToastMethods.showToast(
            i18n.t("toast:studentLicense.addInvitesToEmailInvitationBundle.error", {
              targetEmails: notAddedEmails.join(", "),
            }),
            "error",
          );
        }

        return result;
      })
      .catch(() => {
        ToastMethods.showToast(
          i18n.t("toast:studentLicense.addInvitesToEmailInvitationBundle.genericError"),
          "error",
        );

        return {
          success: [],
          failed: targetEmails,
        };
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  // Fetch information on product license associated to an invite link
  fetchInviteLinkLicenseInfo: action((inviteLinkId) => {
    store.setLoading(true);
    return inviteRepository
      .getInvitationLinkLicenseInfo(inviteLinkId)
      .then((licenseInfo: UsersAPI.InviteLinkLicenseInfo) => {
        store.setInviteLinkLicenseInfo(licenseInfo);
        return true;
      })
      .catch(() => {
        store.setInviteLinkLicenseInfo(null);
        ToastMethods.showToast(
          i18n.t("toast:studentLicense.getInviteLinkLicenseInfo.error"),
          "error",
        );
        return false;
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  // Claim product license associated to an invite link
  claimInviteLinkLicense: action((inviteLinkId) => {
    store.setLoading(true);
    return inviteRepository
      .claimInvitationLinkLicense(inviteLinkId)
      .then(() => ({ success: true }))
      .catch(({ errCode }) => ({ success: false, errCode }))
      .finally(() => {
        store.setLoading(false);
      });
  }),
};

const store = observable({
  ...initialState,
  ...stateSetters,
  ...apiRequests,
} as InviteStoreManagementProps);

export const useInviteManagement = (): InviteStoreManagementProps => store;
